import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Plus, Trash2, Loader2, BookOpen, Search as SearchIcon, FolderPlus } from "lucide-react";
import { toast } from "sonner";

export default function KeywordsPage() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [newKw, setNewKw] = useState("");
  const [newCat, setNewCat] = useState("");
  const [newCategoryName, setNewCategoryName] = useState("");
  const [adding, setAdding] = useState(false);
  const [addingCategory, setAddingCategory] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [kw, cat] = await Promise.all([
        api.get("/keywords"),
        api.get("/keywords/categories"),
      ]);
      setItems(kw.data);
      setCategories(cat.data);
      // Default the keyword form category to first available
      if (!newCat && cat.data.length > 0) setNewCat(cat.data[0]);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const add = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post("/keywords", { keyword: newKw, category: newCat });
      toast.success("Keyword ditambahkan");
      setNewKw("");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAdding(false);
    }
  };

  const addCategory = async (e) => {
    e.preventDefault();
    setAddingCategory(true);
    try {
      const { data } = await api.post("/keywords/categories", { name: newCategoryName });
      toast.success(`Kategori "${data.name}" ditambahkan`);
      setNewCategoryName("");
      setCatOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAddingCategory(false);
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/keywords/${id}`);
      toast.success("Keyword dihapus");
      setItems((curr) => curr.filter((k) => k.id !== id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const delCategory = async (name) => {
    try {
      await api.delete(`/keywords/categories/${encodeURIComponent(name)}`);
      toast.success(`Kategori "${name}" dihapus`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const grouped = items
    .filter((k) => k.keyword.toLowerCase().includes(filter.toLowerCase()) || k.category.toLowerCase().includes(filter.toLowerCase()))
    .reduce((acc, k) => {
      acc[k.category] = acc[k.category] || [];
      acc[k.category].push(k);
      return acc;
    }, {});

  return (
    <div className="px-6 py-6 space-y-5" data-testid="keywords-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Kamus Istilah</p>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-slate-500" /> Keyword Dictionary
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {categories.length} kategori · {items.length} istilah aktif untuk tagging otomatis transkrip.
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Dialog open={catOpen} onOpenChange={setCatOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="rounded-sm border-slate-300"
                  data-testid="add-category-button"
                >
                  <FolderPlus className="h-4 w-4 mr-2" /> Tambah Kategori
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-sm" data-testid="add-category-dialog">
                <DialogHeader>
                  <DialogTitle>Tambah Kategori Baru</DialogTitle>
                </DialogHeader>
                <form onSubmit={addCategory} className="space-y-4">
                  <div>
                    <Label htmlFor="cat-name" className="text-xs uppercase tracking-wider text-slate-600">Nama Kategori</Label>
                    <Input
                      id="cat-name"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      required
                      placeholder="Misal: Cyber & Siber, Diplomasi Militer, Bencana Alam"
                      className="mt-1 rounded-sm border-slate-300"
                      data-testid="new-category-input"
                    />
                    <p className="mt-1.5 text-[11px] text-slate-500">
                      Kategori baru akan langsung tersedia di dropdown saat menambah keyword.
                    </p>
                  </div>
                  <DialogFooter>
                    <Button
                      type="submit"
                      disabled={addingCategory}
                      className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white shadow-none"
                      data-testid="submit-category-button"
                    >
                      {addingCategory && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Simpan
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white shadow-none" data-testid="add-keyword-button">
                  <Plus className="h-4 w-4 mr-2" /> Tambah Keyword
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-sm" data-testid="add-keyword-dialog">
                <DialogHeader>
                  <DialogTitle>Tambah Keyword Baru</DialogTitle>
                </DialogHeader>
                <form onSubmit={add} className="space-y-4">
                  <div>
                    <Label htmlFor="kw-input" className="text-xs uppercase tracking-wider text-slate-600">Keyword</Label>
                    <Input id="kw-input" value={newKw} onChange={(e) => setNewKw(e.target.value)} required className="mt-1 rounded-sm border-slate-300" data-testid="new-keyword-input" />
                  </div>
                  <div>
                    <Label htmlFor="cat-input" className="text-xs uppercase tracking-wider text-slate-600">Kategori</Label>
                    <select
                      id="cat-input"
                      value={newCat}
                      onChange={(e) => setNewCat(e.target.value)}
                      className="mt-1 w-full rounded-sm border border-slate-300 bg-white text-sm px-2 py-1.5"
                      data-testid="new-keyword-category"
                    >
                      {categories.length === 0 && (
                        <option value="">— Belum ada kategori, buat dulu —</option>
                      )}
                      {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <DialogFooter>
                    <Button
                      type="submit"
                      disabled={adding || categories.length === 0}
                      className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white shadow-none"
                      data-testid="submit-keyword-button"
                    >
                      {adding && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Simpan
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </header>

      <div className="bg-white border border-slate-200 rounded-sm flex items-center px-3">
        <SearchIcon className="h-4 w-4 text-slate-500" />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter keyword atau kategori..."
          className="flex-1 bg-transparent text-sm px-3 py-2.5 focus:outline-none"
          data-testid="keywords-filter-input"
        />
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : (
        <div className="space-y-5">
          {(() => {
            // Union of registered categories + any categories actually used by keywords
            const allCats = Array.from(new Set([
              ...categories,
              ...Object.keys(grouped),
            ])).sort();
            const visibleCats = allCats.filter((cat) => {
              if (!filter) return true;
              const fLower = filter.toLowerCase();
              if (cat.toLowerCase().includes(fLower)) return true;
              return (grouped[cat] || []).length > 0;
            });
            if (visibleCats.length === 0) {
              return <p className="text-center text-sm text-slate-500 py-10">Tidak ada kategori atau keyword cocok dengan filter.</p>;
            }
            return visibleCats.map((cat) => {
              const kws = grouped[cat] || [];
              return (
                <div key={cat} className="bg-white border border-slate-200 rounded-sm" data-testid={`kw-cat-${cat}`}>
                  <div className="px-4 py-2.5 border-b border-slate-200 flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold tracking-tight">{cat}</p>
                    <div className="flex items-center gap-3">
                      <span className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">
                        {kws.length} istilah
                      </span>
                      {isAdmin && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button
                              className="text-slate-400 hover:text-red-600 transition-colors"
                              title="Hapus kategori"
                              data-testid={`delete-category-${cat}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="rounded-sm" data-testid={`delete-category-dialog-${cat}`}>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Hapus kategori "{cat}"?</AlertDialogTitle>
                              <AlertDialogDescription className="text-sm text-slate-600">
                                {kws.length > 0 ? (
                                  <>
                                    Kategori ini masih dipakai oleh <b>{kws.length} keyword</b>.
                                    Hapus atau pindahkan keyword tersebut dulu sebelum menghapus kategorinya.
                                  </>
                                ) : (
                                  <>Kategori ini kosong dan akan dihapus dari daftar.</>
                                )}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="rounded-sm" data-testid={`delete-category-cancel-${cat}`}>Batal</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => delCategory(cat)}
                                disabled={kws.length > 0}
                                className="rounded-sm bg-red-600 hover:bg-red-700 text-white"
                                data-testid={`delete-category-confirm-${cat}`}
                              >
                                Ya, Hapus
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                  <div className="p-4 flex flex-wrap gap-2 min-h-[44px]">
                    {kws.length === 0 ? (
                      <p className="text-xs text-slate-400 italic">Belum ada keyword pada kategori ini.</p>
                    ) : kws.map((k) => (
                      <span
                        key={k.id}
                        className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-2.5 py-1 text-sm"
                        data-testid={`keyword-${k.keyword}`}
                      >
                        {k.keyword}
                        {isAdmin && (
                          <button
                            onClick={() => del(k.id)}
                            className="text-slate-400 hover:text-red-600"
                            title="Hapus"
                            data-testid={`delete-keyword-${k.id}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}
    </div>
  );
}
